/**
 * Fetching a file the assistant produced and saving it to the machine.
 *
 * The file is fetched with the session token rather than linked to directly:
 * the download endpoint needs the caller's credentials, and a plain link
 * cannot carry them.
 */

/** The API root. Every call in the chat goes through it. */
export const API_BASE = '/api';

/**
 * Downloads a file the assistant produced.
 *
 * The response is turned into an object URL and handed to a link, which is
 * what makes the browser save it under its own name rather than open it.
 *
 * The URL is revoked once the browser has taken the blob. Revoking it
 * immediately would cancel the download in some browsers, so it is left for a
 * moment and then released -- a leaked object URL keeps the whole file in
 * memory for as long as the page lives.
 */
export async function downloadChatFile(
  fileId: string,
  filename: string,
  token: string,
): Promise<void> {
  const response = await fetch(`${API_BASE}/ai/files/${fileId}`, {
    headers: { Authorization: `Bearer ${token}` },
  });

  if (!response.ok) {
    const body = await response.json().catch(() => ({}));
    throw new Error(body.error || body.message || `Download failed: ${response.status}`);
  }

  const blob = await response.blob();
  const url = URL.createObjectURL(blob);

  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = filename;
  anchor.rel = 'noopener';
  // Firefox only honours the download name of a link that is in the document.
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();

  setTimeout(() => URL.revokeObjectURL(url), 1000);
}
